import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import { showToast } from 'stores/ToastStore';
import { findUsers } from 'utils/UserWebAPIUtils';
import Pagination from 'components/v2/PaginationV2';
import AddButton from 'components/v2/buttons/AddButton';
import Table from 'components/v2/Table';
import { getYards, searchYardsByNameAndAddress } from 'utils/YardAPIUtils';
import Spinner from 'components/ui/Spinner';
import MarketsAPIUtils from 'utils/MarketsAPIUtils';
import CargomaticInput from 'components/v2/input';
import {
  Actions,
  createYardsListColumns,
  breadcrumbs,
  ITEMS_PER_PAGE_LIMIT,
  SEARCH_DELAY,
  setYardsListHeaders,
} from './constants';

const Loading = function () {
  return (
    <div
      data-testid="yardsListLoading"
      className="absolute justify-center w-full"
      style={{ top: '50%' }}
    >
      <div className="align-middle">
        <span className="text-5xl text-green">
          <Spinner showMessage={false} />
        </span>
      </div>
    </div>
  );
};

const transformYards = (yardsResponse, markets) => {
  const { total, yards } = yardsResponse;

  return {
    total,
    yards: yards.map((yard) => {
      return {
        ...yard,
        marketCode: markets.find((market) => market.id === yard.marketId)?.code,
      };
    }),
  };
};

const initialState = {
  payload: {
    sortBy: 'nameInternal',
    sortOrder: 'ascend',
    skip: null,
    limit: ITEMS_PER_PAGE_LIMIT,
    yardInput: { id: null, marketId: null, nameInternal: null, status: null },
  },
  activePage: 1,
  yardsResponse: { yards: [], total: 0 },
  searchText: null,
  isLoading: true,
  markets: [],
};

const reducer = (state, action) => {
  switch (action.type) {
    case Actions.SET_IS_LOADING:
      return { ...state, isLoading: action.payload };
    case Actions.SET_YARDS_RESPONSE:
      return {
        ...state,
        yardsResponse: action.payload,
        isLoading: false,
      };
    case Actions.SET_ACTIVE_PAGE:
      return { ...state, activePage: action.payload };
    case Actions.SET_FETCH_PAYLOAD:
      return { ...state, payload: action.payload };
    case Actions.SET_SEARCH_INPUT_VALUE:
      return {
        ...state,
        activePage: 1,
        searchText: action.payload,
        payload: {
          ...state.payload,
          skip: null,
        },
      };
    case Actions.SET_MARKETS_RESPONSE:
      return {
        ...state,
        markets: action.payload,
        isLoading: false,
      };
    default:
      return state;
  }
};

export default function YardsList() {
  const { t } = useTranslation();
  const history = useHistory();

  const [yardsState, dispatch] = useReducer(reducer, initialState, undefined);
  const [vendors, setVendors] = useState({});

  const searchYards = async (searchText, sortBy, sortOrder, offset, limit) => {
    dispatch({ type: Actions.SET_IS_LOADING, payload: true });
    const yardsResponse = await searchYardsByNameAndAddress(
      searchText,
      sortBy,
      sortOrder,
      offset,
      limit,
    );

    dispatch({
      type: Actions.SET_YARDS_RESPONSE,
      payload: transformYards(
        yardsResponse.data.searchYardsByNameAndAddress,
        yardsState.markets,
      ),
    });
  };

  const fetchYards = async (
    sortBy,
    sortOrder,
    skip,
    limit,
    yardInput = { id: null, marketId: null, nameInternal: null, status: null },
  ) => {
    dispatch({ type: Actions.SET_IS_LOADING, payload: true });
    const yardsResponse = await getYards(
      sortBy,
      sortOrder,
      skip,
      limit,
      yardInput,
    );

    dispatch({
      type: Actions.SET_YARDS_RESPONSE,
      payload: transformYards(yardsResponse.data.getYards, yardsState.markets),
    });
  };

  useEffect(() => {
    async function fetchData() {
      const res = await MarketsAPIUtils.getMarketsV2();

      dispatch({
        type: Actions.SET_MARKETS_RESPONSE,
        payload: res,
      });
    }
    fetchData();
  }, []);

  useEffect(() => {
    const { searchText, payload, markets } = yardsState;

    if (markets.length) {
      if (!searchText) {
        fetchYards(...Object.values(yardsState.payload));
      } else {
        const { sortBy, sortOrder, limit } = payload;
        const timeOutId = setTimeout(
          () => searchYards(searchText, sortBy, sortOrder, payload.skip, limit),
          SEARCH_DELAY,
        );

        return () => clearTimeout(timeOutId);
      }
    }
  }, [yardsState.searchText, yardsState.payload, yardsState.markets]);

  useEffect(() => {
    fetchUsers();
  }, [yardsState.yardsResponse.yards]);

  async function fetchUsers() {
    dispatch({ type: Actions.SET_IS_LOADING, payload: true });
    const venderIds = yardsState?.yardsResponse?.yards.map(
      (yard) => yard?.vendor,
    );
    try {
      if (venderIds.length) {
        const {
          entity: { items },
        } = await findUsers({ _id: venderIds });
        const vendors = items.reduce((_vendors, _vendor) => {
          _vendors[_vendor._id] = {
            account_name:
              _vendor._type === 'Carrier' ? _vendor.name : _vendor.account_name,
          };
          return _vendors;
        }, {});
        setVendors(vendors);
      }
    } catch (error) {
      showToast(t('NOT_FOUND'), t('USERS_NOT_FOUND'), 'error');
    } finally {
      dispatch({ type: Actions.SET_IS_LOADING, payload: false });
    }
  }

  const handleInputChange = (val) => {
    dispatch({
      type: Actions.SET_SEARCH_INPUT_VALUE,
      payload: val,
    });
  };

  const handlePageChange = (eventKey) => {
    dispatch({ type: Actions.SET_ACTIVE_PAGE, payload: eventKey });
    const { searchText, payload } = yardsState;
    const skip = ITEMS_PER_PAGE_LIMIT * (eventKey - 1);

    if (searchText) {
      const { sortBy, sortOrder, limit } = payload;
      searchYards(searchText, sortBy, sortOrder, skip, limit);

      return;
    }

    dispatch({
      type: Actions.SET_FETCH_PAYLOAD,
      payload: {
        ...payload,
        skip,
      },
    });
  };

  const renderFooter = () => {
    return (
      <tr>
        <td colSpan={8} className="p-0">
          <Pagination
            className="border-t-0"
            perPage={ITEMS_PER_PAGE_LIMIT}
            page={yardsState.activePage}
            pages={Math.ceil(
              yardsState.yardsResponse.total / ITEMS_PER_PAGE_LIMIT,
            )}
            total={yardsState.yardsResponse.total}
            showCount
            paginationContainerClassName="mx-auto my-0 sticky right-2/4"
            onPageClick={(eventKey) => handlePageChange(eventKey)}
          />
        </td>
      </tr>
    );
  };

  return (
    <LayoutWithSidebar
      className="pt-3"
      title={t('YARD_MANAGEMENT')}
      breadcrumbs={breadcrumbs}
    >
      <div className="flex items-center space-x-3 mb-4">
        <h3>{t('MANAGE_YARDS')}</h3>
        <div data-testid="addNewYardBtnWrapper" className="flex justify-end">
          <AddButton onClick={() => history.push('/yards/new')} />
        </div>
      </div>

      <div className="mt-4 mb-2">
        <CargomaticInput
          type="search"
          label={t('TOTAL_SHIPMENT_WEIGHT')}
          placeholder={t('TOTAL_SHIPMENT_WEIGHT')}
          onChange={(val) => handleInputChange(val)}
        />
      </div>
      <div className="overflow-x-auto table-wrapper">
        {yardsState.isLoading ? (
          <Loading />
        ) : (
          <Table
            headers={setYardsListHeaders(t)}
            columns={createYardsListColumns(history, t, vendors)}
            data={yardsState.yardsResponse.yards}
            footer={renderFooter}
          />
        )}
      </div>
    </LayoutWithSidebar>
  );
}
